import Constants from '@/Constants';
import { ContentRequest } from '@/models/ContentRequest';
import Item, { ItemPutRequest, ItemRequest } from '@/models/Item';
import { LinkMetadata } from '@/models/LinkMetadata';
import { UpdateContentData } from '@/models/UpdateContentData';
import http from '@/services/HttpService';
import { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import { call, delay, put } from 'redux-saga/effects';
import { PayloadAction } from 'typesafe-actions';
import _ from 'underscore';
import { ContentStudentView } from '../content/types';
import { store } from '../index';
import { updateContentStudentViewInCourses } from '../list/action';
import {
    archiveItemFailure,
    archiveItemSuccess,
    clearCurrentContentStudentViewFromListInProgress,
    contentCategorizedSuccess,
    contentCompletedSuccess,
    contentFavoriteSuccess,
    contentForYouSuccess,
    contentInProgressSuccess,
    contentLikeYouSuccess,
    contentMostAccessedSuccess,
    contentsEssentialsSuccess,
    contentsLastPublishedSuccess,
    contentsSearchedSuccess,
    contentsSuggestedSuccess,
    contentTopRatedSuccess,
    contentTrainingsSuccess,
    deleteItemFailure,
    deleteItemSuccess,
    finishContentFailure,
    finishContentSuccess,
    getContentFailure,
    getContentListFailure,
    getContentSuccess,
    getItemFailure,
    getItemPathSuccess,
    getItemSuccess,
    getItensFailure,
    getItensSuccess,
    postItemFailure,
    postItemSuccess,
    publishItemFailure,
    publishItemSuccess,
    putItemFailure,
    putItemSuccess,
    refreshItem,
    setContentLinkMetadataFailure,
    setContentLinkMetadataSuccess,
    startContentFailure,
    startContentSuccess,
    toggleItemVisibilityFailure,
    toggleItemVisibilitySuccess,
    updateContentStudentViewFailure,
    updateContentStudentViewSuccess,
} from './action';
import { ContentStudentViewRequest, ItemTypes } from './types';

export function* getItem(action: PayloadAction<ItemTypes.GET_ITEM_REQUEST, { contentId: number; refreshOnlyPath: boolean }>) {
    try {
        const response = yield call(http.get, `${Constants.ITENS_URI}/${action.payload.contentId}`);

        if (action.payload.refreshOnlyPath) {
            yield put(getItemPathSuccess(response.data.path));
        } else {
            yield put(getItemSuccess(response.data));
        }
    } catch (err) {
        yield put(getItemFailure(err));
    }
}

export function* getItens(action: PayloadAction<ItemTypes.GET_ITEMS_REQUEST, ItemRequest>) {
    try {
        const {
            applicationId,
            orgId,
            maxWorkload,
            mediaType,
            minWorkload,
            name,
            status,
            visibility,
            orderBy,
            locale,
            moduleId,
            offset,
            limit,
        } = action.payload;

        let request = `${Constants.ITENS_URI}?applicationId=${applicationId}&organizationId=${orgId}`;

        if (name) {
            request += `&name=${name}`;
        }
        if (mediaType) {
            request += `&mediaType=${mediaType}`;
        }
        if (minWorkload) {
            request += `&minWorkload=${minWorkload}`;
        }
        if (maxWorkload) {
            request += `&maxWorkload=${maxWorkload}`;
        }
        if (visibility) {
            request += `&visibility=${visibility}`;
        }
        if (status) {
            request += `&status=${status}`;
        }
        if (orderBy) {
            request += `&orderBy=${orderBy}`;
        }
        if (locale) {
            request += `&locale=${locale}`;
        }
        if (moduleId) {
            request += `&moduleId=${moduleId}`;
        }
        if (_.isNumber(offset)) {
            request += `&offset=${offset}`;
        }
        if (_.isNumber(limit)) {
            request += `&limit=${limit}`;
        }

        const { data, headers } = yield call(http.get, request);

        yield put(getItensSuccess(data, parseInt(headers.total)));
    } catch (err) {
        yield put(getItensFailure(err));
    }
}

export function* postItem(action: PayloadAction<ItemTypes.POST_ITEM_REQUEST, { item: Item; willPublish: boolean }>) {
    const {
        locale: {
            data: { toastMessages },
        },
        identity: {
            data: {
                user: { orgId },
            },
        },
    } = store.getState();

    try {
        const { data } = yield call(http.post, `${Constants.ITENS_URI}`, action.payload.item);
        yield put(postItemSuccess(data));
        toast.success(toastMessages.ITEM_SAVE_SUCCESS);

        if (action.payload.item.mediaType === 'VIDEO') {
            try {
                yield call(http.post, `${Constants.TRACKING_AWS}`, {
                    appId: Constants.APP_ID,
                    orgId,
                    entityType: 'CONTENT',
                    fileKey: data.path,
                    entityId: data.id,
                    outputPath: `bagagem/assets/content/${data.identifier}/`,
                    locale: data.locale,
                });
            } catch (err) {}
        }

        if (action.payload.willPublish) {
            yield call(http.post, `${Constants.ITENS_URI}/${data.id}/publish`);
        }
    } catch (err) {
        toast.error(toastMessages.ITEM_SAVE_ERROR);
        yield put(postItemFailure(err));
    }
}

export function* putItem(action: PayloadAction<ItemTypes.PUT_ITEM_REQUEST, ItemPutRequest>) {
    try {
        const {
            locale: {
                data: { toastMessages },
            },
            identity: {
                data: {
                    user: { orgId },
                },
            },
        } = store.getState();

        const { item, videoUpdate } = action.payload;

        yield call(http.put, `${Constants.ITENS_URI}/${item.id}`, item);
        yield put(putItemSuccess());
        toast.success(toastMessages.ITEM_SAVE_SUCCESS);

        if (item.mediaType === 'VIDEO' && videoUpdate === true) {
            try {
                yield call(http.post, `${Constants.TRACKING_AWS}`, {
                    appId: Constants.APP_ID,
                    orgId,
                    entityType: 'CONTENT',
                    fileKey: item.path,
                    entityId: item.id,
                    outputPath: `bagagem/assets/content/${item.identifier}/`,
                    locale: item.locale,
                });
            } catch (err) {}
        }
    } catch (err) {
        yield put(putItemFailure(err));
    }
}

export function* deleteItem(action: PayloadAction<ItemTypes.DELETE_ITEM_REQUEST, number>) {
    try {
        const {
            identity: {
                data: {
                    user: { orgId, applicationId },
                },
            },
            locale: {
                data: { toastMessages },
            },
        } = store.getState();

        yield call(http.delete, `${Constants.ITENS_URI}/${action.payload}`);
        const { data, headers } = yield call(http.get, `${Constants.ITENS_URI}?applicationId=${applicationId}&organizationId=${orgId}&status=DRAFT`);

        yield put(deleteItemSuccess(action.payload));
        toast.success(toastMessages.CONTENT_REMOVE_SUCCESS);
        yield put(refreshItem());
        yield put(getItensSuccess(data, parseInt(headers.total)));
    } catch (err) {
        yield put(deleteItemFailure(err));
    }
}

export function* publishItem(action: PayloadAction<ItemTypes.PUBLISH_ITEM_REQUEST, { id: number; status: string }>) {
    try {
        const {
            identity: {
                data: {
                    user: { orgId, applicationId },
                },
            },
        } = store.getState();

        yield call(http.post, `${Constants.ITENS_URI}/${action.payload.id}/publish`);
        const { data, headers } = yield call(
            http.get,
            `${Constants.ITENS_URI}?applicationId=${applicationId}&organizationId=${orgId}&status=${action.payload.status}`,
        );

        yield put(publishItemSuccess(action.payload.id));
        yield put(getItensSuccess(data, parseInt(headers.total)));
    } catch (err) {
        yield put(publishItemFailure(err));
    }
}

export function* archiveItem(action: PayloadAction<ItemTypes.ARCHIVE_ITEM_REQUEST, number>) {
    try {
        const {
            identity: {
                data: {
                    user: { orgId, applicationId },
                },
            },
        } = store.getState();

        yield call(http.post, `${Constants.ITENS_URI}/${action.payload}/archive`);
        const { data, headers } = yield call(
            http.get,
            `${Constants.ITENS_URI}?applicationId=${applicationId}&organizationId=${orgId}&status=PUBLISHED`,
        );

        yield put(archiveItemSuccess(action.payload));
        yield put(getItensSuccess(data, parseInt(headers.total)));
    } catch (err) {
        yield put(archiveItemFailure(err));
    }
}

let requestCounter: number = 0;
export function* getContentListRequest(action: PayloadAction<ItemTypes.GET_CONTENT_LIST_REQUEST, ContentRequest>) {
    try {
        yield delay(requestCounter++ * Constants.SAGA_DELAY);

        const {
            personId,
            paginationData,
            contentEnrollmentStatus,
            categoryId,
            contentIds,
            listTypeParm,
            term,
            randomizeRecommendations,
            courseId,
            categoryIds,
            tagIds,
            locales,
        } = action.payload;

        let queryParams: string = `?offset=${paginationData.offset}&limit=${paginationData.limit}`;
        let path: string = Constants.LMS_CONTENTS_URI.replace('%p', personId.toString());

        if (paginationData.orderBy) {
            queryParams += `&orderBy=${paginationData.orderBy}`;
        }

        if (listTypeParm === 'FOR_YOU' || listTypeParm === 'LIKE_YOU') {
            path = Constants.CONTENTS_URI;
            queryParams += `&type=${listTypeParm}`;
            queryParams += `&randomizeRecommendations=${randomizeRecommendations}`;
        }

        if (listTypeParm === 'SUGGESTED_CONTENTS_TO_PLAYLIST' || listTypeParm === 'SEARCH' || listTypeParm === 'SEARCH_CONTENTS_TO_PLAYLIST') {
            path = Constants.CAST_CONTENTS_SEARCH_URI;

            if (listTypeParm === 'SUGGESTED_CONTENTS_TO_PLAYLIST') {
                queryParams += `&type=${listTypeParm}`;
            }

            if (term != undefined) {
                queryParams += `&term=${term}`;
            }

            if (courseId !== undefined) {
                queryParams += `&courseId=${courseId}`;
            }

            if (categoryIds !== undefined) {
                queryParams += `&categoryIds=${categoryIds}`;
            }

            if (tagIds !== undefined) {
                queryParams += `&tagIds=${tagIds}`;
            }

            if (locales !== undefined) {
                queryParams += `&locales=${locales}`;
            }
        }

        if (contentEnrollmentStatus !== undefined) {
            queryParams += `&contentEnrollmentStatus=${contentEnrollmentStatus}`;
        }

        if (categoryId !== undefined) {
            queryParams += `&categoryId=${categoryId}`;
        }

        if (contentIds !== undefined) {
            queryParams += `&contentIds=${contentIds}`;
        }

        let response = {
            data: [],
            headers: {
                total: '0',
            },
        };
        const request: AxiosRequestConfig = { headers: { requestSuccessType: paginationData.contentRequestSuccessType } };

        if (paginationData.doRequest) {
            response = yield call(http.get, path + queryParams, request);
        }

        switch (request.headers.requestSuccessType) {
            case ItemTypes.LAST_PUBLISHED_SUCCESS:
                yield put(contentsLastPublishedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.MOST_ACCESSED_SUCCESS:
                yield put(contentMostAccessedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.IN_PROGRESS_SUCCESS:
                yield put(contentInProgressSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.COMPLETED_SUCCESS:
                yield put(contentCompletedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.TOP_RATED_SUCCESS:
                yield put(contentTopRatedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.CATEGORIZED_SUCCESS:
                yield put(contentCategorizedSuccess(response.data, parseInt(response.headers.total), categoryId));
                break;
            case ItemTypes.FAVORITES_SUCCESS:
                yield put(contentFavoriteSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.LIKE_YOU_SUCCESS:
                yield put(contentLikeYouSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.FOR_YOU_SUCCESS:
                yield put(contentForYouSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.SEARCHED_SUCCESS:
                yield put(contentsSearchedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.SUGGESTED_SUCCESS:
                yield put(contentsSuggestedSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.ESSENTIALS_SUCCESS:
                yield put(contentsEssentialsSuccess(response.data, parseInt(response.headers.total)));
                break;
            case ItemTypes.TRAININGS_SUCCESS:
                yield put(contentTrainingsSuccess(response.data, parseInt(response.headers.total)));
                break;
            default:
                break;
        }
    } catch (error) {
        yield put(getContentListFailure());
    } finally {
        requestCounter--;
    }
}

export function* toggleItemVisibility(action: PayloadAction<ItemTypes.TOGGLE_ITEM_VISIBILITY_REQUEST, number>) {
    try {
        yield call(http.post, `${Constants.ITENS_URI}/${action.payload}/toggle-visibility`);
        yield put(toggleItemVisibilitySuccess(action.payload));
    } catch (err) {
        yield put(toggleItemVisibilityFailure());
    }
}

export function* getContentRequest(action: PayloadAction<ItemTypes.GET_CONTENT_REQUEST, ContentStudentViewRequest>) {
    try {
        const { personId, contentId, locale } = action.payload;
        const path: string = Constants.CONTENT_STUDENT_VIEW_URI.replace('%p', personId.toString()).replace('%c', contentId.toString());

        const { data } = yield call(http.get, path, { headers: { locale } });
        yield put(getContentSuccess(data));
    } catch (error) {
        yield put(getContentFailure());
    }
}

export function* startContentRequest(action: PayloadAction<ItemTypes.START_CONTENT_REQUEST, ContentRequest>) {
    try {
        const { contentId } = action.payload;
        yield call(http.post, Constants.START_CONTENT_URI.replace('%s', contentId.toString()));
        yield put(startContentSuccess(contentId));
    } catch (error) {
        yield put(startContentFailure());
    }
}

export function* finishContentRequest(action: PayloadAction<ItemTypes.FINISH_CONTENT_REQUEST, ContentRequest>) {
    try {
        const { contentId } = action.payload;
        yield call(http.post, Constants.FINISH_CONTENT_URI.replace('%s', contentId.toString()));
        yield put(clearCurrentContentStudentViewFromListInProgress(contentId));
        yield put(finishContentSuccess(contentId));
    } catch (error) {
        yield put(finishContentFailure());
    }
}

export function* updateContentStudentViewRequest(action: PayloadAction<ItemTypes.UPDATE_CONTENT_STUDENT_VIEW_REQUEST, UpdateContentData>) {
    try {
        const { personId, contentId } = action.payload;
        const path = Constants.CONTENT_STUDENT_VIEW_URI.replace('%p', personId.toString()).replace('%c', contentId.toString());
        const response = yield call(http.get, path);
        const contentStudentView: ContentStudentView = response.data;
        yield put(updateContentStudentViewSuccess(contentStudentView));
        yield put(updateContentStudentViewInCourses(contentStudentView));
    } catch (error) {
        yield put(updateContentStudentViewFailure());
    }
}
export function* setContentLinkMetadataRequest(action: PayloadAction<ItemTypes.SET_CONTENT_LINK_METADATA_REQUEST, string>) {
    try {
        const [protocol, url] = action.payload.split('://');
        const path: string = Constants.TRACKING_METADATA_URI;
        const queryParams: string = `?protocol=${protocol}&url=${url}`;

        const response = yield call(http.get, path + queryParams);
        const linkMetadata: LinkMetadata = response.data;

        yield put(setContentLinkMetadataSuccess(linkMetadata));
    } catch (error) {
        yield put(setContentLinkMetadataFailure());
    }
}
